import React from "react";
import ObjectList from "./ObjectList";
import ObjectSettingsConstants from "../lib/ObjectSettingsConstants";
import _ from "underscore";

type TDropdownOption = {
    id: number | string;
    name?: string;
    extid?: string;
    options?: TDropdownOption[];
};

type TObjectListWithDropdownProps = {
    /** Objects to list; a nested array is a group of related objects. */
    items?: any[];
    editable?: boolean;
    /** When set (non-null), the edit button replaces the remove button. */
    propertyName?: string | null;
    editFunction?: React.MouseEventHandler;
    hideEditButton?: boolean;
    hidePlusButton?: boolean;
    /** Optional <select> rendered above the list; omit for no dropdown. */
    dropdownOptions?: {
        options?: TDropdownOption[];
        headlineValue?: string;
    };
    onOptionSelected?: (event: React.ChangeEvent<HTMLSelectElement>, items: any[]) => void;
    onItemsChanged?: (items: any[]) => void;
    onSelection?: (object: any) => void;
    /** Keep `items` order instead of the order the search returns. */
    preserveOrder?: boolean;
    /** Search model used to resolve items; untyped throughout the object code. */
    searchObject?: any;
    updateSearchObject?: (...args: any[]) => void;
    /**
     * Resolves the objects available for a parent value. Only reachable when
     * `searchObject.parentValue` is set, which only ObjectDefinition does.
     */
    getAvailabilityObjects?: (parentValue?: any) => any;
    totalNumber?: number | null;
    /**
     * Renders the membership-period suffix for an object. Passing `_.noop` is
     * meaningful downstream: ObjectList guards on truthiness, so a no-op blanks
     * the membership column rather than falling through to another renderer.
     */
    presentMembershipPeriod?: (objectId: number) => string | null | void;
    cellRenderers?: any;
    hoverButton?: React.ReactNode;
    extraRightSideMenuButton?: React.ReactNode;
    allowMultiSelection?: boolean;
    flex?: boolean;
    // Passed by callers but not read here; kept so call sites type-check.
    label?: string;
    hasPeriodObjects?: boolean;
};

const ObjectListWithDropdown = (props: TObjectListWithDropdownProps) => {
    let items = props.items || [];
    let dropdown: React.JSX.Element | null = null;
    let selection: any = null;
    const onOptionSelected = props.onOptionSelected || _.noop;
    const onItemsChanged = props.onItemsChanged || _.noop;
    const dropdownOptions = props.dropdownOptions;
    const onSelection = props.onSelection || _.noop;
    const preserveOrder = props.preserveOrder || false;

    const onItemClick = function (index, rowData, event, object) {
        if (object) {
            onSelection(object);
        } else {
            selection = parseInt(index.target ? index.target.value : index, 10);
            onSelection(items[selection]);
        }
    };
    const onMinusClick = function () {
        const value = selection;
        items = items.filter((item) => {
            if (item instanceof Array) {
                return value !== item.map((subItem) => subItem.id).join(",");
            }
            return item.id !== parseInt(value, 10);
        });
        onItemsChanged(items);
    };
    if (dropdownOptions && dropdownOptions.options instanceof Array) {
        const onSelect = (event) => onOptionSelected(event, items);
        const options = dropdownOptions.options.map((item) => {
            if (item.options) {
                return (
                    <optgroup key={item.name} label={item.name}>
                        {item.options.map((subOption) => (
                            <option key={subOption.id} value={`${item.id}:${subOption.id}`}>
                                {subOption.name || subOption.extid}
                            </option>
                        ))}
                    </optgroup>
                );
            }
            return (
                <option key={item.id} value={item.id}>
                    {item.name || item.extid}
                </option>
            );
        });
        if (dropdownOptions.headlineValue) {
            options.splice(
                0,
                0,
                <option key={-1} value={"DUMMY"}>
                    {dropdownOptions.headlineValue}
                </option>
            );
        }
        dropdown = (
            <select disabled={!props.editable} className="optionDropdown" onChange={onSelect}>
                {options}
            </select>
        );
    }
    items = items || [];
    const options = items.map((item) => {
        // Use pure ids, not TimeEdit JSON objects.
        if (item instanceof Array) {
            // Related objects is an array of arrays.
            // eslint-disable-next-line no-param-reassign
            item = item[0];
        }
        return item.id;
    });
    let editButton: React.JSX.Element | null = null;
    if (props.editable && !props.hideEditButton) {
        editButton = <div className="minusIcon" onClick={onMinusClick} />;
        if (props.propertyName !== null) {
            editButton = <div className="editIcon" onClick={props.editFunction} />;
        }
    }

    let searchObject = props.searchObject;
    const objectToNameAndId = (object) => {
        const name =
            object.fields && object.fields[0].values
                ? object.fields[0].values.join(", ")
                : object.extid || object.id;
        const appendix = props.presentMembershipPeriod
            ? props.presentMembershipPeriod(object.id)
            : "";

        const label = appendix ? `${name} ${appendix}` : name;
        return _.extend({}, object, {
            label,
        });
    };

    const sort = function (objects) {
        if (!preserveOrder) {
            return [...objects];
        }
        const result: any[] = [];
        objects.forEach((object) => {
            result[options.indexOf(object.id)] = object;
        });
        return result;
    };

    const loadFunction = function (objects, callback) {
        if (searchObject) {
            if (searchObject.parentValue && props.getAvailabilityObjects) {
                // eslint-disable-next-line no-param-reassign
                objects = props
                    .getAvailabilityObjects(searchObject.parentValue)
                    .map((object) => object.id);
            }
            searchObject = searchObject.immutableSet({
                searchObjects: objects,
                excludeObjects: [],
                otherObjectsMode: ObjectSettingsConstants.OTHER_OBJECTS.INCLUDE,
            });

            searchObject.search(0, (result) => {
                callback(sort(result.map(objectToNameAndId)));
            });
        } else {
            callback(sort(items.map(objectToNameAndId)));
        }
    };

    let title = "";
    if (searchObject && searchObject.defaultColumns && searchObject.defaultColumns.length > 0) {
        title = searchObject.defaultColumns[0].name;
    }

    const flexStyle = props.flex ? { display: "flex", flex: "1 1 auto" } : {};
    return (
        <div style={flexStyle}>
            {editButton}
            {dropdown}
            {props.extraRightSideMenuButton}
            <ObjectList
                flex={props.flex}
                columnTitle={title}
                listSize={3}
                cssClass={"definitionList"}
                loadFunction={loadFunction}
                searchObject={searchObject}
                cellRenderers={props.cellRenderers || undefined}
                updateSearchObject={props.updateSearchObject}
                totalNumber={props.totalNumber}
                objects={options}
                hidePlusButton={props.hidePlusButton}
                onChange={props.editable ? onItemClick : _.noop}
                presentMembershipPeriod={props.presentMembershipPeriod}
                hoverButton={props.hoverButton}
                allowMultiSelection={props.allowMultiSelection || false}
            />
        </div>
    );
};

export default ObjectListWithDropdown;
